import Link from "next/link";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import "aos/dist/aos.css";
import useProduct from "@/hooks/useProduct";
import ProductItem from "@/components/products/ProductItem";
import { IProductType } from "@/typings/Props";
import { useRouter } from "next/router";
import { media } from "@/styles/media";

export default function OtherModels() {
  const { t } = useTranslation();
  const products = useProduct();
  const router = useRouter();

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (products.state.products.length === 0) {
      products.fetchProductsAsync();
    }
  }, [products.state.products]);

  useEffect(() => {
    setCurrentPage(1);
  }, [router.asPath]);

  const productsPerPage = 5;
  const startIndex = (currentPage - 1) * productsPerPage;
  const currentProducts = products.state.products.slice(startIndex, startIndex + productsPerPage);
  const totalPages = Math.ceil(products.state.products.length / productsPerPage);

  return (
    <OtherModelsWrapper>
      <TitleWrapper>
        <Title className="title" data-aos="fade-right" data-aos-duration="1000">
          {t("menu.products.other-models.title")}
        </Title>
        <Button className="button" href="/all-products">
          {t("menu.products.other-models.view-all")}
        </Button>
      </TitleWrapper>
      <ProductWrapper>
        {currentProducts.map((item: IProductType) => {
          return <ProductItem key={item.id} {...item} />;
        })}
      </ProductWrapper>
      <PaginationWrapper>
        <PageButton onClick={() => setCurrentPage((prev) => prev - 1)} disabled={currentPage === 1}>
          {t("menu.products.other-models.previous")}
        </PageButton>
        {Array.from({ length: totalPages }, (_, index) => (
          <PageButton key={index + 1} $active={currentPage === index + 1} onClick={() => setCurrentPage(index + 1)}>
            {index + 1}
          </PageButton>
        ))}
        <PageButton onClick={() => setCurrentPage((prev) => prev + 1)} disabled={currentPage === totalPages}>
          {t("menu.products.other-models.next")}
        </PageButton>
      </PaginationWrapper>
    </OtherModelsWrapper>
  );
}

const OtherModelsWrapper = styled.div`
  padding: 50px;

  ${media.desktop`
    padding: 40px 3%;
  `}
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 50px 0;

  ${media.tablet`
    flex-direction: column;
  `}
`;

const Title = styled.p`
  width: 100%;
  padding: 40px 0;
  font-family: "Lato Bold";
  font-size: 2.75rem;
  font-weight: 700;
  color: #444444;
  display: flex;
  justify-content: space-between;

  ${media.tablet`
    padding: 10px;
    text-align: center;
  `}
  ${media.phablet`
    font-size: 2rem;
  `}
  ${media.mini`
    font-size: 1.5rem;
  `}
`;

const Button = styled(Link)`
  width: 200px;
  height: 40px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 0.9rem;
  color: white;
  background-color: black;
  border: 1px solid black;
  border-radius: 20px;
  transition: 0.3s ease-out;
  cursor: pointer;

  &:hover {
    color: black;
    background-color: white;
    transition: 0.3s ease-out;
  }

  ${media.tablet`
    margin: 15px auto 0 0;
    padding: 0.8em 2.2em;
    width: initial;
    height: initial;
  `}
`;

const ProductWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;

  ${media.desktop`
    height: initial;
  `}
`;

// Pagination
const PaginationWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 10px 15px;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${({ $active }) => ($active ? "white" : "#444444")};
  background-color: ${({ $active }) => ($active ? "#971b2f" : "#f7f7f7")};
  border: none;
  cursor: pointer;
`;
